import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Briefcase, Loader2 } from "lucide-react";
import {
  useEmploymentPeriods,
  useCreateEmploymentPeriod,
  useDeleteEmploymentPeriod,
  EmploymentPeriod,
} from "@/hooks/useEmploymentPeriods";
import { formatDate } from "@/lib/date-utils";

interface EmploymentPeriodFormProps {
  personId: string;
}

export function EmploymentPeriodForm({ personId }: EmploymentPeriodFormProps) {
  const { data: periods = [], isLoading } = useEmploymentPeriods(personId);
  const createPeriod = useCreateEmploymentPeriod();
  const deletePeriod = useDeleteEmploymentPeriod();

  const [percentage, setPercentage] = useState("100");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");

  const handleAdd = () => {
    const pct = parseInt(percentage);
    if (!pct || !validFrom) return;
    createPeriod.mutate({
      person_id: personId,
      percentage: pct,
      valid_from: validFrom,
      valid_to: validTo || null,
    });
    setPercentage("100");
    setValidFrom("");
    setValidTo("");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          Employment Periods
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="w-20">
            <Label className="text-xs">%</Label>
            <Input
              type="number"
              min="1"
              max="100"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs">From</Label>
            <DatePicker value={validFrom} onChange={(v) => setValidFrom(v)} />
          </div>
          <div className="flex-1">
            <Label className="text-xs">To (optional)</Label>
            <DatePicker
              value={validTo}
              onChange={(v) => setValidTo(v)}
              placeholder="Optional"
            />
          </div>
          <Button
            size="icon"
            onClick={handleAdd}
            disabled={createPeriod.isPending || !validFrom || !percentage}
          >
            {createPeriod.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : periods.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            No periods configured (defaults to 100%)
          </p>
        ) : (
          <div className="space-y-1">
            {periods.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50"
              >
                <div className="text-sm">
                  <span className="font-medium">{p.percentage}%</span>
                  <span className="text-muted-foreground ml-2">
                    {formatDate(p.valid_from)}
                    {p.valid_to ? ` - ${formatDate(p.valid_to)}` : " - ongoing"}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => deletePeriod.mutate({ id: p.id, personId })}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
